import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Phone, MapPin, Package, Clock, MessageCircle, UserCog,
  Copy, Share2, Truck as TruckIcon, Calendar, CalendarClock, CreditCard, Banknote,
  Bell, Send,
} from "lucide-react";
import { api, money, formatApiError } from "../../lib/api";
import { whatsappUrl } from "../../lib/whatsapp";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import TrackingMap from "../../components/TrackingMap";
import AssignDriverDialog from "../../components/AssignDriverDialog";
import { useCompany } from "../../contexts/CompanyContext";
import { formatEta } from "../../lib/eta";
import { formatDateLong, scheduleUrgency, shortOrderId } from "../../lib/format";
import { computeStageEvents, totalElapsedMs, fmtElapsedClock, elapsedTone } from "../../lib/stageTiming";
import { canTransition, nextHappyStatus } from "../../lib/orderStatus";
import { BottomSheet } from "../../components/ui/bottom-sheet";

const STATUSES = [
  { v: "pending", t: "Recibido" },
  { v: "confirmed", t: "Confirmado" },
  { v: "on_the_way", t: "En camino" },
  { v: "delivered", t: "Entregado" },
  { v: "cancelled", t: "Cancelado" },
  // Eventos auxiliares (no son estados reales del pedido pero sí
  // entradas válidas del historial cuando entra el Modo AI):
  { v: "driver_assigned", t: "Unidad asignada" },
  { v: "ai_no_driver", t: "AI: sin unidades disponibles" },
  { v: "ai_failed", t: "AI: error en pipeline" },
];
const STATUS_COLOR = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-blue-100 text-blue-800",
  on_the_way: "bg-violet-100 text-violet-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-800",
};
const KIND_LABEL = {
  order_received: "Pedido recibido",
  order_confirmed: "Pedido confirmado",
  order_on_the_way: "En camino",
  order_delivered: "Entregado",
};

/** Traducción de payment_status crudo del backend → etiqueta usuario. */
const PAYMENT_STATUS_LABEL = {
  pending: "Pendiente",
  cash_on_delivery: "Pago contra entrega",
  paid: "Pagado",
  failed: "Fallido",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};
const fmtPaymentStatus = (s) => PAYMENT_STATUS_LABEL[s] || (s ? s.replace(/_/g, " ") : "—");

/**
 * Devuelve la etiqueta de "actor" para una entrada del status_history
 * (timeline). Sólo se muestra a operadores admin — la vista pública
 * y la del cliente ya estriban estos campos del backend (`/public/track`).
 *
 *   - admin con nombre  → `[Juan Pérez]`
 *   - admin sin nombre  → `[Admin]`
 *   - bot WhatsApp      → `[Bot WhatsApp]`
 *   - cliente / sin actor → null (no se muestra)
 */
const fmtActor = (ev) => {
  if (!ev) return null;
  if (ev.by === "system" || ev.by_role === "ai") return `[Auto-piloto AI]`;
  if (ev.by_name && ev.by !== "client") return `[${ev.by_name}]`;
  if (ev.by === "admin" || ev.by_role === "company_admin" || ev.by_role === "super_admin") return "[Admin]";
  if (ev.by === "bot") return "[Bot WhatsApp]";
  return null;
};

/**
 * Live timer "since reception" — FREEZES on delivered/cancelled showing
 * the final total elapsed (end_at - created_at) en lugar de desaparecer.
 * Usa `totalElapsedMs` del helper compartido.
 */
function ElapsedSince({ order, big }) {
  const [now, setNow] = useState(() => Date.now());
  const closed = order?.status === "delivered" || order?.status === "cancelled";
  useEffect(() => {
    if (closed) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [closed]);
  const ms = totalElapsedMs(order, now);
  if (ms == null) return null;
  const tone = elapsedTone(ms, { closed });
  return (
    <span
      className={`font-mono tabular-nums ${big ? "text-2xl font-extrabold" : "text-sm font-semibold"} ${tone}`}
      title={closed ? "Tiempo total desde recepción hasta cierre" : "Tiempo desde la recepción"}
    >
      {fmtElapsedClock(ms)}
    </span>
  );
}

/** Lista vertical de tiempos por status. En el detalle usamos un layout
 *  más grande que en la lista/dashboard: chip + "hace X". */
function StageAgesDetail({ order }) {
  const [now, setNow] = useState(() => Date.now());
  const closed = order?.status === "delivered" || order?.status === "cancelled";
  useEffect(() => {
    if (closed) return;
    const t = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(t);
  }, [closed]);
  const events = computeStageEvents(order, now);
  if (events.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid="stage-ages-detail">
      {events.map((ev) => (
        <span
          key={ev.status + ev.at}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-neutral-100 border border-neutral-200"
        >
          <span className="font-semibold text-neutral-800">{ev.label}</span>
          <span className="text-neutral-500">hace {ev.ageLabel}</span>
        </span>
      ))}
    </div>
  );
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentCompany } = useCompany();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [pendingNotifs, setPendingNotifs] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [eta, setEta] = useState(null);
  // Bottom-sheet cancellation flow with predefined motives. The reason is
  // appended to the status_history payload for auditing.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelOther, setCancelOther] = useState("");
  const [showServiceArea, setShowServiceArea] = useState(false);
  const fetchedOnce = useRef(false);

  const load = async () => {
    try {
      const r = await api.get(`/orders/${id}`);
      setOrder(r.data);
      fetchedOnce.current = true;
    } catch (e) {
      if (!fetchedOnce.current) {
        toast.error(formatApiError(e.response?.data?.detail) || "Pedido no encontrado");
        navigate("/admin/orders");
      }
    }
    api.get(`/orders/${id}/tracking`).then((r) => setTracking(r.data)).catch(() => {});
    api.get(`/wa/notifications?order_id=${id}`).then((r) => {
      setPendingNotifs((r.data || []).filter((n) => n.status === "pending"));
    }).catch(() => setPendingNotifs([]));
  };

  useEffect(() => {
    load();
    // Live refresh every 8 s so admin sees driver updates and any status
    // change pushed by other channels (bot, client cancel) without manually
    // reloading the page.
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!order) {
    return (
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-8">
        <div className="h-32 bg-neutral-100 rounded-2xl animate-pulse" />
      </main>
    );
  }

  const updateStatus = async (status) => {
    if (!canTransition(order.status, status)) {
      toast.error(`No puedes ir de "${order.status}" a "${status}"`);
      return;
    }
    if (status === "on_the_way" && !order.driver_id) {
      toast.error("Asigna primero una unidad de reparto antes de marcar el pedido como En camino.");
      return;
    }
    try {
      await api.patch(`/orders/${order.order_id}/status`, { status });
      toast.success("Estado actualizado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const sendWaNotif = async (n) => {
    try {
      await api.post(`/wa/notifications/${n.wa_notif_id}/sent`);
      toast.success("Mensaje marcado como enviado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const dismissWaNotif = async (n) => {
    try {
      await api.post(`/wa/notifications/${n.wa_notif_id}/dismiss`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Quick-action handlers used by the right sidebar. They mirror the more
  // verbose flows (status select, WA list) but are surfaced as 1-click
  // shortcuts for operators in rush hours.
  const quickAdvance = async () => {
    const next = nextHappyStatus(order.status);
    if (!next) return;
    await updateStatus(next);
  };
  const resendLastWa = async () => {
    try {
      const r = await api.get(`/wa/notifications?order_id=${order.order_id}`);
      const list = r.data || [];
      const last = list[0];
      if (!last) {
        toast.error("Aún no hay mensajes para este pedido");
        return;
      }
      await api.post(`/wa/notifications/${last.wa_notif_id}/retry`);
      toast.success("Mensaje WhatsApp reencolado");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const submitCancel = async () => {
    if (order.status === "cancelled" || order.status === "delivered") return;
    const reason = cancelReason === "Otro" ? cancelOther.trim() : cancelReason;
    if (!reason) {
      toast.error("Selecciona o escribe un motivo");
      return;
    }
    try {
      await api.patch(`/orders/${order.order_id}/status`, {
        status: "cancelled",
        reason,
      });
      toast.success("Pedido cancelado");
      setCancelOpen(false);
      setCancelReason("");
      setCancelOther("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const trackUrl = `${window.location.origin}/track/${order.order_id}`;
  const waMsg = `Hola${order.client_name ? ` ${order.client_name}` : ""}! 🔥 Tu pedido #${shortOrderId(order.order_id)} está ${STATUSES.find((s) => s.v === order.status)?.t || order.status}. Síguelo aquí: ${trackUrl}`;
  const waHref = whatsappUrl(order.client_phone, waMsg);

  const destination = order.address?.lat && order.address?.lng
    ? { lat: order.address.lat, lng: order.address.lng }
    : null;
  const driverLoc = tracking?.driver?.current_lat && tracking?.driver?.current_lng
    ? { lat: tracking.driver.current_lat, lng: tracking.driver.current_lng }
    : null;
  const channel = tracking?.driver?.kind || (order.items?.[0]?.channel) || "cilindrera";
  const primary = currentCompany?.primary_color || "#111";
  const contrast = currentCompany?.primary_color_contrast || "#fff";
  const tz = currentCompany?.timezone;
  const showMap = !!destination;
  const etaInfo = formatEta(eta, currentCompany?.eta_tolerance_min || 0);
  const driverSnap = order.driver_snapshot || tracking?.driver;

  const copyTrack = async () => {
    try { await navigator.clipboard.writeText(trackUrl); toast.success("Link copiado"); }
    catch (_e) { toast.error("No se pudo copiar"); }
  };

  return (
    <main className="max-w-6xl mx-auto px-4 md:px-6 py-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn" aria-label="Atrás">
          <ArrowLeft size={18} />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-neutral-400 font-mono">Pedido #{shortOrderId(order.order_id)}</p>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight truncate">
            {STATUSES.find((s) => s.v === order.status)?.t || order.status}
          </h1>
        </div>
        <span className={`hidden md:inline-flex text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_COLOR[order.status]}`}>
          {STATUSES.find((s) => s.v === order.status)?.t}
        </span>
      </div>

      {/* Status row: timer + status switcher + quick actions. Cada columna
          tiene la misma estructura "label + control h-10" para que el
          espaciado vertical quede idéntico al alinear con items-end. */}
      <div className="mt-5 bg-white rounded-2xl border border-neutral-200 p-5 flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="min-w-[180px]">
          <p className="text-xs text-neutral-400 uppercase tracking-widest mb-1">
            {(order.status === "delivered" || order.status === "cancelled")
              ? "Tiempo total del pedido"
              : "Tiempo desde la recepción"}
          </p>
          <div className="h-10 flex items-center">
            <ElapsedSince order={order} big />
          </div>
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-xs text-neutral-400 uppercase tracking-widest mb-1">Cambiar estado</p>
          <Select value={order.status} onValueChange={updateStatus}>
            <SelectTrigger className="w-full md:w-56 h-10" data-testid="admin-order-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => {
                const blockedNoDriver = s.v === "on_the_way" && !order.driver_id;
                const disabled = !canTransition(order.status, s.v) || blockedNoDriver;
                return (
                  <SelectItem
                    key={s.v}
                    value={s.v}
                    disabled={disabled}
                    data-testid={`status-option-${s.v}`}
                  >
                    {s.t}{blockedNoDriver ? " (asigna unidad)" : ""}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        <div className="md:ml-auto">
          <p className="text-xs text-neutral-400 uppercase tracking-widest mb-1">Contacto</p>
          <div className="flex flex-wrap gap-2">
            {waHref && (
              <Button asChild variant="outline" className="h-10 text-green-700 border-green-200 hover:bg-green-50">
                <a href={waHref} target="_blank" rel="noreferrer" data-testid="wa-client-btn">
                  <MessageCircle size={14} className="mr-1.5" /> WhatsApp al cliente
                </a>
              </Button>
            )}
            {order.client_phone && (
              <Button asChild variant="outline" className="h-10" data-testid="call-client-btn">
                <a href={`tel:${order.client_phone}`}>
                  <Phone size={14} className="mr-1.5" /> Llamar
                </a>
              </Button>
            )}
            <Button variant="outline" className="h-10" onClick={copyTrack} data-testid="copy-track-btn">
              <Copy size={14} className="mr-1.5" /> Copiar link
            </Button>
          </div>
        </div>
      </div>

      {/* Etapas — edad de cada transición (recibido/confirmado/en camino/
          entregado o cancelado). Útil para ver cuánto lleva el pedido en
          cada fase de un solo vistazo. */}
      <div className="mt-3">
        <StageAgesDetail order={order} />
      </div>

      {/* Pending WA notifications for THIS order */}
      {pendingNotifs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="wa-pending-list">
          {pendingNotifs.map((n) => (
            <div key={n.wa_notif_id} className="flex items-center gap-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 dark:text-green-200 rounded-xl px-3 py-2.5">
              <Bell size={14} className="text-green-700 shrink-0" />
              <span className="text-sm text-green-900 flex-1 min-w-0 truncate">
                <span className="font-semibold">{KIND_LABEL[n.kind] || n.kind}:</span> mensaje WhatsApp listo
              </span>
              <Button size="sm" variant="outline" className="text-green-700 border-green-300 hover:bg-green-100"
                      onClick={() => sendWaNotif(n)} data-testid={`wa-send-${n.wa_notif_id}`}>
                <Send size={12} className="mr-1" /> Enviar
              </Button>
              <button onClick={() => dismissWaNotif(n)} className="text-xs text-green-700/70 hover:text-green-900 underline">
                descartar
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Two-column layout: main info + sidebar */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* ---- Left: main content ---- */}
        <div className="space-y-6">
          {/* Map */}
          {showMap && (
            <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden fade-in">
              <TrackingMap
                driverLocation={driverLoc}
                destination={destination}
                channel={channel}
                addressType={order.address?.type}
                color={primary}
                contrast={contrast}
                status={order.status}
                height={320}
                onEtaChange={setEta}
                serviceAreaPolygons={showServiceArea ? (currentCompany?.service_area_polygons || null) : null}
              />
              {order.status === "on_the_way" && etaInfo && (
                <div className="px-4 py-3 border-t border-neutral-100 flex items-center gap-2 text-sm">
                  <TruckIcon size={14} className="text-neutral-500" />
                  <span className="text-neutral-700">Llegada estimada al cliente:</span>
                  <span
                    className="ml-auto inline-flex items-center font-bold text-xs px-2.5 py-1 rounded-full text-white"
                    style={{ background: "var(--company-primary)" }}
                    data-testid="admin-eta-pill"
                  >
                    ~{etaInfo.label}
                  </span>
                </div>
              )}
              {/* Toggle Área de cobertura — debajo del mapa para que el
                  operador pueda visualizar si la dirección cae adentro de
                  los polígonos definidos por la empresa. Off por default. */}
              <div className="px-4 py-3 border-t border-neutral-100 flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold flex items-center gap-1.5">
                    <MapPin size={13} className="text-neutral-500" /> Área de cobertura
                  </p>
                  <p className="text-xs text-neutral-500 leading-snug">
                    {(currentCompany?.service_area_polygons || []).length === 0
                      ? "Esta empresa aún no tiene polígonos de cobertura definidos."
                      : "Dibuja sobre el mapa los polígonos de servicio de la empresa."}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={showServiceArea}
                  disabled={(currentCompany?.service_area_polygons || []).length === 0}
                  onClick={() => setShowServiceArea((v) => !v)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    showServiceArea ? "bg-[color:var(--company-primary)]" : "bg-neutral-300"
                  }`}
                  data-testid="toggle-service-area"
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
                      showServiceArea ? "translate-x-5" : "translate-x-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Items */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Package size={18} /> Productos
            </h2>
            <div className="mt-3 divide-y divide-neutral-100">
              {order.items.map((i, idx) => (
                <div key={idx} className="flex items-center justify-between py-2.5 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold truncate">{i.name}</p>
                    <p className="text-xs text-neutral-500">
                      {i.quantity} {i.unit ? i.unit : "u"} × {money(i.unit_price)}
                    </p>
                  </div>
                  <span className="font-bold whitespace-nowrap">{money(i.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t flex items-center justify-between text-base">
              <span className="font-semibold">Total</span>
              <span className="font-display font-extrabold text-xl">{money(order.total)}</span>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5">
            <h2 className="font-display font-bold text-lg flex items-center gap-2">
              <Clock size={18} /> Línea de tiempo
            </h2>
            <ol className="mt-3 space-y-2.5">
              {(order.status_history || []).map((ev, i) => {
                const actor = fmtActor(ev);
                return (
                <li key={i} className="flex items-start gap-3 text-sm">
                  <span className={`h-2 w-2 rounded-full mt-1.5 shrink-0 ${ev.status === "cancelled" ? "bg-red-500" : "bg-company"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{STATUSES.find((s) => s.v === ev.status)?.t || ev.status}</span>
                      {ev.auto === "ai_geofence" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200">
                          geocerca
                        </span>
                      )}
                      <span
                        className="ml-auto text-xs text-neutral-500 font-mono whitespace-nowrap"
                        data-testid={`timeline-meta-${ev.status}-${i}`}
                      >
                        {actor && (
                          <span className="text-neutral-700 font-semibold mr-1">{actor}</span>
                        )}
                        {formatDateLong(ev.at, tz)}
                      </span>
                    </div>
                    {ev.reason && (
                      <p className={`text-xs mt-0.5 ${ev.status === "cancelled" ? "text-red-600" : "text-neutral-500"}`}>
                        Motivo: {ev.reason}
                      </p>
                    )}
                  </div>
                </li>
                );
              })}
              {(order.status_history || []).length === 0 && (
                <li className="text-sm text-neutral-500">Sin eventos registrados.</li>
              )}
            </ol>
          </div>
        </div>

        {/* ---- Right sidebar ---- */}
        <aside className="space-y-4">
          {/* Quick actions — 1-click shortcuts for the admin during rush
              hour: advance status, resend last WhatsApp, cancel with reason.
              They are intentionally *separate* from the detailed Select +
              WA list above so the operator doesn't have to scroll/think. */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-2">
            <p className="text-xs text-neutral-400 uppercase tracking-widest mb-1">Acciones rápidas</p>
            {(() => {
              const next = nextHappyStatus(order.status);
              const advanceLabel = {
                confirmed: "Confirmar",
                on_the_way: "Marcar en camino",
                delivered: "Marcar entregado",
              }[next];
              const blockedNoDriver = next === "on_the_way" && !order.driver_id;
              return advanceLabel ? (
                <Button
                  className="w-full h-10 justify-start font-semibold"
                  style={{ background: "var(--company-primary)", color: "var(--company-primary-contrast)" }}
                  onClick={quickAdvance}
                  disabled={blockedNoDriver}
                  title={blockedNoDriver ? "Asigna primero una unidad de reparto" : undefined}
                  data-testid="quick-advance-btn"
                >
                  {advanceLabel}{blockedNoDriver ? " · asigna unidad" : ""}
                </Button>
              ) : null;
            })()}
            <Button
              variant="outline"
              className="w-full h-10 justify-start text-green-700 hover:bg-green-50 border-green-200"
              onClick={resendLastWa}
              data-testid="resend-wa-btn"
            >
              <Send size={14} className="mr-2" /> Reenviar último mensaje WA
            </Button>
            {order.status !== "cancelled" && order.status !== "delivered" && (
              <Button
                variant="outline"
                className="w-full h-10 justify-start text-red-600 hover:bg-red-50 border-red-200"
                onClick={() => setCancelOpen(true)}
                data-testid="cancel-order-btn"
              >
                Cancelar pedido…
              </Button>
            )}
          </div>

          {/* Customer */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5">
            <p className="text-xs text-neutral-400 uppercase tracking-widest">Cliente</p>
            <p className="font-display font-bold text-lg mt-1 truncate">{order.client_name || "—"}</p>
            {order.client_phone && (
              <a href={`tel:${order.client_phone}`} className="text-sm text-neutral-600 hover:underline mt-1 inline-flex items-center gap-1.5">
                <Phone size={12} /> {order.client_phone}
              </a>
            )}
          </div>

          {/* Driver */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-neutral-400 uppercase tracking-widest">Repartidor</p>
              <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} data-testid="assign-driver-btn">
                <UserCog size={12} className="mr-1" />{order.driver_id ? "Reasignar" : "Asignar"}
              </Button>
            </div>
            {driverSnap ? (
              <div className="mt-3">
                <p className="font-display font-bold truncate">{driverSnap.name}</p>
                {driverSnap.vehicle_plate && (
                  <p className="text-xs text-neutral-500">{driverSnap.vehicle_plate}</p>
                )}
                {driverSnap.phone && (
                  <a href={`tel:${driverSnap.phone}`} className="text-xs text-neutral-600 hover:underline inline-flex items-center gap-1 mt-1">
                    <Phone size={10} /> {driverSnap.phone}
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 mt-3">Sin asignar</p>
            )}
          </div>

          {/* Delivery */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-3">
            <div>
              <p className="text-xs text-neutral-400 uppercase tracking-widest flex items-center gap-1">
                <MapPin size={11} /> Dirección
              </p>
              <p className="text-sm font-semibold mt-1">{order.address?.full_address || "—"}</p>
              {order.address?.reference && (
                <p className="text-xs text-neutral-500 mt-0.5">Ref: {order.address.reference}</p>
              )}
              {order.distance_km != null && (
                <p className={`text-xs mt-1 font-semibold ${order.out_of_service_area ? "text-red-600" : "text-neutral-500"}`}>
                  {order.distance_km} km desde bodega{order.out_of_service_area ? " · Fuera del área de servicio" : ""}
                </p>
              )}
            </div>
            <div className="text-sm">
              <p className="text-xs text-neutral-400 uppercase tracking-widest flex items-center gap-1">
                <Calendar size={11} /> Entrega
              </p>
              {(() => {
                const u = scheduleUrgency(order, tz);
                const tone = {
                  immediate: { text: "text-neutral-700",  chip: "" },
                  upcoming:  { text: "text-blue-900",     chip: "bg-blue-50 text-blue-700 border border-blue-200" },
                  today:     { text: "text-amber-900",    chip: "bg-amber-50 text-amber-800 border border-amber-200" },
                  overdue:   { text: "text-red-800 font-bold", chip: "bg-red-50 text-red-700 border border-red-200" },
                }[u];
                const chipLabel = u === "overdue" ? "Vencido"
                  : u === "today" ? "Hoy"
                  : u === "upcoming" ? "Programado"
                  : null;
                return (
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap" data-testid={`order-detail-schedule-${u}`}>
                    <p className={tone.text}>
                      {order.when === "now" || !order.scheduled_at
                        ? "Inmediata"
                        : formatDateLong(order.scheduled_at, tz)}
                    </p>
                    {chipLabel && (
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tone.chip}`}>
                        <CalendarClock size={10} />{chipLabel}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="text-sm">
              <p className="text-xs text-neutral-400 uppercase tracking-widest flex items-center gap-1">
                {order.payment_method === "cash" ? <Banknote size={11} /> : <CreditCard size={11} />} Pago
              </p>
              <p className="mt-0.5">
                {order.payment_method === "cash" ? "Efectivo" : "En línea"} · {fmtPaymentStatus(order.payment_status)}
              </p>
            </div>
            {order.notes && (
              <div className="text-sm">
                <p className="text-xs text-neutral-400 uppercase tracking-widest">Notas del cliente</p>
                <p className="mt-0.5">{order.notes}</p>
              </div>
            )}
          </div>

          {/* Public tracking */}
          <div className="bg-white rounded-2xl border border-neutral-200 p-5">
            <p className="text-xs text-neutral-400 uppercase tracking-widest flex items-center gap-1">
              <Share2 size={11} /> Link de seguimiento
            </p>
            <p className="text-xs text-neutral-600 break-all mt-2">{trackUrl}</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={copyTrack}>
                <Copy size={12} className="mr-1" /> Copiar
              </Button>
              <Button size="sm" asChild variant="outline">
                <Link to={`/track/${order.order_id}`} target="_blank" rel="noreferrer">Abrir</Link>
              </Button>
            </div>
          </div>
        </aside>
      </div>

      <AssignDriverDialog
        orderId={assignOpen ? order.order_id : null}
        orderStatus={order.status}
        open={assignOpen}
        onOpenChange={(o) => setAssignOpen(o)}
        onAssigned={() => { setAssignOpen(false); load(); }}
      />

      {/* Cancellation BottomSheet — predefined motives + free text fallback.
          The selected reason is sent to the backend with the cancelled
          status update so it lands in the order audit trail. */}
      <BottomSheet
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancelar pedido"
        description="Selecciona el motivo. Esto cambiará el estado del pedido a Cancelado."
      >
        <div className="px-4 sm:px-6 pb-6 space-y-2">
          {[
            "Cliente no responde",
            "Sin stock",
            "Fuera de horario",
            "Dirección fuera del área",
            "Pago rechazado",
            "Otro",
          ].map((opt) => (
            <button
              key={opt}
              onClick={() => setCancelReason(opt)}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-colors tap-fade ${
                cancelReason === opt
                  ? "border-red-300 bg-red-50 text-red-800 font-semibold"
                  : "border-neutral-200 hover:bg-neutral-50"
              }`}
              data-testid={`cancel-reason-${opt}`}
            >
              {opt}
            </button>
          ))}
          {cancelReason === "Otro" && (
            <textarea
              autoFocus
              value={cancelOther}
              onChange={(e) => setCancelOther(e.target.value)}
              placeholder="Describe brevemente el motivo…"
              className="w-full mt-2 px-3 py-2.5 rounded-xl border border-neutral-200 focus:border-red-400 outline-none text-sm min-h-[80px]"
              data-testid="cancel-reason-other-textarea"
            />
          )}
          <div className="flex gap-2 pt-3">
            <Button
              variant="outline"
              className="flex-1 h-11"
              onClick={() => setCancelOpen(false)}
              data-testid="cancel-sheet-back"
            >
              Volver
            </Button>
            <Button
              className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white"
              onClick={submitCancel}
              disabled={!cancelReason || (cancelReason === "Otro" && !cancelOther.trim())}
              data-testid="cancel-sheet-submit"
            >
              Cancelar pedido
            </Button>
          </div>
        </div>
      </BottomSheet>
    </main>
  );
}
